import type { JSX } from 'react';

import { AppDialog } from '@/components/ui/app-dialog.js';
import { Button } from '@/components/ui/button.js';
import { DialogClose } from '@/components/ui/dialog.js';

interface ConfirmDeleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  /** Name shown in the confirmation copy. */
  name: string;
  /** What kind of thing is being deleted, used in the title and copy ("Delete {entityLabel}?").
   *  Omit it where the thing has no noun a person would use — the copy then names the entry
   *  itself ("Delete “Daily ping”?"), which is what the Scheduled section does. */
  entityLabel?: string;
  /** An extra sentence appended after the standard "can't be undone" line, for callers whose
   *  deletion has consequences beyond the record itself (e.g. secrets stored elsewhere). */
  extraWarning?: string;
  /** Disables the Delete button — pass the delete mutation's `isPending` (or `isPending ||
   *  isSuccess`) so a slow request can't be fired twice by a second click before the dialog closes. */
  confirmDisabled?: boolean;
}

/**
 * Shared "delete X?" confirmation, used by the Scheduled list row/detail page and by the quality
 * project Environments section. Uses AppDialog (not window.confirm — suppressed in the Teams iframe
 * per project UI conventions).
 */
export function ConfirmDeleteDialog({
  open,
  onOpenChange,
  onConfirm,
  name,
  entityLabel,
  extraWarning,
  confirmDisabled,
}: ConfirmDeleteDialogProps): JSX.Element {
  const title = entityLabel ? `Delete ${entityLabel}?` : `Delete “${name}”?`;
  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      titleHidden
      showCloseButton={false}
      className="flex w-full max-w-[var(--dialog-w-sm)] flex-col gap-0 overflow-hidden rounded-xl border-border/50 p-0 shadow-xl"
    >
      <div className="px-6 pt-6 pb-2">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold tracking-tight text-foreground">
            {title}
          </h3>
          <DialogClose asChild>
            <button
              type="button"
              aria-label="Close"
              className="-mt-1 -mr-1 flex size-7 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-foreground/10 hover:text-foreground"
            >
              <span aria-hidden>×</span>
            </button>
          </DialogClose>
        </div>
        <p className="mt-2 text-compact leading-relaxed text-muted-foreground">
          {entityLabel ? `Delete ${entityLabel} “${name}”?` : title} This can't
          be undone.
          {extraWarning ? ` ${extraWarning}` : ''}
        </p>
      </div>
      <div className="flex items-center justify-end gap-2 px-6 pb-5 pt-4">
        <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button
          variant="destructive-outline"
          size="sm"
          onClick={onConfirm}
          disabled={confirmDisabled}
        >
          Delete
        </Button>
      </div>
    </AppDialog>
  );
}
